import { inject, Injectable } from '@angular/core';
import {
  ExtendedMap,
  JepLayer,
  LayerInsertionDetails,
  LayerResults,
  PluginMap
} from '@jeppesen-foreflight/jmkw-types';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { JmkwApiService } from '../../../jmkw-api.service';

@Injectable({ providedIn: 'root' })
export class LayerFactory {
  private readonly _api = inject(JmkwApiService);
  private readonly _layerCache = new ExtendedMap<JepLayer>();

  addPluginLayers$(pluginMap: PluginMap): Observable<LayerResults> {
    const newLayers = this.getUncachedLayers(pluginMap);

    if (newLayers.length === 0) {
      return of({});
    }

    const insertionDetails = this.insertLayers(newLayers);
    return this.loadLayers(insertionDetails);
  }

  private getUncachedLayers(pluginMap: PluginMap): [string, JepLayer][] {
    return [...pluginMap.allLayers().entries()].filter(
      ([id]) => !this._layerCache.has(id)
    );
  }

  private insertLayers(layers: [string, JepLayer][]): LayerInsertionDetails[] {
    const map = this._api.getMap();
    const existingLayers = map.layers.toArray() as JepLayer[];
    const insertionDetails: LayerInsertionDetails[] = [];

    layers.forEach(([id, layer]) => {
      try {
        const insertIndex = this.findInsertIndex(layer, existingLayers);
        const finalIndex =
          insertIndex === -1 ? existingLayers.length : insertIndex;
        map.add(layer, finalIndex);
        this._layerCache.set(id, layer);
        existingLayers.splice(finalIndex, 0, layer);

        insertionDetails.push({
          layer,
          id,
          insertIndex: finalIndex,
          zOrder: layer.zOrder ?? 0
        });
      } catch (error) {
        console.error(`Failed to insert layer ${id}:`, error);
      }
    });

    return insertionDetails;
  }

  private findInsertIndex(layer: JepLayer, existingLayers: JepLayer[]): number {
    return existingLayers.findIndex(
      (existing) => (existing.zOrder ?? 0) > (layer.zOrder ?? 0)
    );
  }

  private loadLayers(
    details: LayerInsertionDetails[]
  ): Observable<LayerResults> {
    return from(
      Promise.all(
        details.map(({ layer, id, insertIndex, zOrder }) =>
          layer
            .load()
            .then(() => ({
              [id]: {
                success: true,
                zOrder,
                insertIndex
              }
            }))
            .catch((error) => {
              this._layerCache.delete(id);
              return {
                [id]: {
                  success: false,
                  zOrder,
                  insertIndex,
                  error: error?.message || 'Layer failed to load'
                }
              };
            })
        )
      )
    ).pipe(map((results) => Object.assign({}, ...results)));
  }
}
